import { useEffect, useState } from 'react';
import { ViewStyle } from 'react-native';
import { useMutation } from 'react-query';
import { Box, CheckIcon, HStack, IconButton, Pressable, VStack } from 'native-base';
import { startCase, toLower } from 'lodash';
import { useTranslation } from 'react-i18next';

import { CalendarIcon, FileAttachmentIcon, Modal, SwitchButton, Text, UserPenIcon } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { AddUpdateCleaningTaskAssignment, DeleteCleaningTaskAssignment } from '@pimm/services/lib/sms-workforce/services';
import { formatTo, formatToISOString, formatToTimeOnly, stringToDateLocal } from '@app/utils/date-formatter';
import {
  AddUpdateTaskAssignmentRequest,
  CleaningScheduleTypeEnum,
  CleaningTaskStatusEnum,
  DeleteCleaningTaskAssignmentQueryParams,
  Employee,
} from '@pimm/services/lib/sms-workforce/types';
import { useSiteTime } from '@app/features/store-core';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { useCleaningAssignments } from '../context';
import { CleaningTaskAssignee, CleaningTaskItem } from '../reducers';
import { PressableSelect } from './pressable-select';
import { ConfirmIncompleteTask } from './confirm-incomplete-task';
import { PopoverBulkScheduleMenu } from './popover-bulk-schedule-menu';
import { EmployeeAssignment } from './employee-assignment';
import { getScheduleColor } from '../utils';
import { useCleaningToast } from '../hooks';

type CleaningTasksTableItemProps = {
  scheduleDate: Date;
  scheduleType: CleaningScheduleTypeEnum;
  task: CleaningTaskItem;
  isEditable?: boolean;
  /** DOP start hour sourced from dopBlocks[blockNumber=1].startTime. Defaults to 5. */
  dopStartHour?: number;
  onOpenLinkDocument?: (task: CleaningTaskItem) => void;
};

export const CleaningTasksTableItem = ({
  isEditable = false,
  scheduleDate,
  scheduleType,
  task,
  dopStartHour = 5,
  ...props
}: CleaningTasksTableItemProps) => {
  const { translate } = useAppLocale();
  const { t } = useTranslation();
  const siteTime = useSiteTime();
  const { siteId, updateCleaningTask } = useCleaningAssignments();
  const { showToast } = useCleaningToast();
  const modalEmployee = useModalFocus<CleaningTaskItem>();
  const modalConfirm = useModalFocus();
  const modalScheduleTime = useModalFocus();
  const [assignee, setAssignee] = useState<CleaningTaskAssignee | undefined>(task.assignee);

  const currentWeek = siteTime.toStartEndOfWeek(scheduleDate);
  const employeeName = startCase(toLower([assignee?.firstName, assignee?.lastName].filter(Boolean).join(' ')));
  const isDailyCleaning = scheduleType === CleaningScheduleTypeEnum.Daily;
  const isCompleted = assignee?.status === CleaningTaskStatusEnum.Complete;
  const isNotApplicable = assignee?.status === CleaningTaskStatusEnum.NotApplicable;

  const scheduleColor = getScheduleColor(scheduleType);

  const addUpdateCleaningTask = useMutation({
    mutationFn: async (payload: AddUpdateTaskAssignmentRequest) => {
      const result = await AddUpdateCleaningTaskAssignment({ ...payload, cleaningTaskId: task.id, siteId: siteId });
      return result;
    },
  });

  const deleteCleaningTask = useMutation({
    mutationFn: async (payload: DeleteCleaningTaskAssignmentQueryParams) => {
      const result = await DeleteCleaningTaskAssignment({ ...payload });
      return result;
    },
  });

  const addUpdateAssignment = async (payload: CleaningTaskAssignee) => {
    const statusDateISO = payload?.statusDate ? formatToISOString(payload?.statusDate) : formatToISOString(currentWeek.startDate);
    const dueDateISO = payload?.dueDate ? formatToISOString(payload?.dueDate) : statusDateISO;

    // Optimistically update the query cache so any background refetch doesn't revert the UI
    updateCleaningTask(scheduleType, { id: task.id, assignee: payload });

    const result = await addUpdateCleaningTask.mutateAsync({
      id: payload?.id,
      cleaningTaskId: task.id,
      cleaningTaskStatus: payload?.status,
      employeeId: payload.employeeId,
      statusDate: statusDateISO,
      dueDate: dueDateISO,
      scheduleType: scheduleType,
      siteId: siteId,
    });

    updateCleaningTask(scheduleType, {
      id: task.id,
      assignee: {
        ...payload,
        id: result.id,
        dueDate: stringToDateLocal(result.dueDate),
        status: result.cleaningTaskStatus,
        statusDate: stringToDateLocal(result.statusDate),
        isAutoScheduled: result.isAutoScheduled,
        isAutoAssigned: result.isAutoAssigned,
      },
    });
  };

  const deleteAssignment = async (assignmentId: number) => {
    await deleteCleaningTask.mutateAsync({ id: assignmentId });
    updateCleaningTask(scheduleType, {
      id: task.id,
      assignee: undefined,
    });
  };

  const handleChangeAssignee = (employee?: Employee, date?: Date) => {
    modalEmployee.setHide();

    if (!employee && assignee?.id) {
      setAssignee(undefined);
      deleteAssignment(assignee.id);
    } else if (employee) {
      const nextAssignee = {
        ...assignee,
        employeeId: employee.id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        dueDate: isDailyCleaning ? assignee?.dueDate ?? scheduleDate : date,
      };
      setAssignee(nextAssignee);
      addUpdateAssignment(nextAssignee);
    }
  };

  const handleDeleteAssignee = () => handleChangeAssignee();

  const handleCompletion = (complete?: boolean) => {
    if (!assignee) return;

    modalConfirm.setHide();

    const today = siteTime.today();
    const statusDate = complete ? today : undefined;
    const status = complete ? CleaningTaskStatusEnum.Complete : CleaningTaskStatusEnum.Incomplete;
    const nextAssignee = {
      ...assignee,
      status: status,
      statusDate,
    };

    setAssignee(nextAssignee);
    addUpdateAssignment(nextAssignee);
  };

  const handleToggleNotApplicable = (value: boolean) => {
    if (!value && !assignee?.employeeId) {
      setAssignee(undefined);
      if (assignee?.id) deleteAssignment(assignee.id);
    } else {
      const dueDate = assignee?.dueDate ?? scheduleDate;
      const nextAssignee = {
        id: assignee?.id,
        employeeId: assignee?.employeeId,
        status: value ? CleaningTaskStatusEnum.NotApplicable : CleaningTaskStatusEnum.Incomplete,
        statusDate: dueDate,
        dueDate,
      };
      setAssignee(nextAssignee);
      addUpdateAssignment(nextAssignee);
    }

    showToast(
      'success',
      <Text size="md" color="white" fontWeight={600}>
        {value ? t('cleaning_task:task_marked_na') : t('cleaning_task:task_restored')}
      </Text>,
    );
  };

  const handleChangeDueDate = (dueDate?: Date) => {
    modalScheduleTime.setHide();
    const nextAssignee = {
      ...assignee,
      dueDate,
      statusDate: scheduleType === 'Daily' ? dueDate : assignee?.statusDate,
    };
    setAssignee(nextAssignee);
    addUpdateAssignment(nextAssignee);
  };

  const handlePressLinkedDocument = () => {
    if (props.onOpenLinkDocument) props.onOpenLinkDocument(task);
  };

  useEffect(() => {
    if (!addUpdateCleaningTask.isLoading && !deleteCleaningTask.isLoading) {
      setAssignee(task.assignee);
    }
  }, [task.assignee, addUpdateCleaningTask.isLoading, deleteCleaningTask.isLoading]);

  return (
    <>
      <HStack
        pl={isEditable ? 4 : 0}
        minH="56px"
        borderBottomWidth={1}
        borderColor="gray.50"
        backgroundColor={isCompleted ? `${scheduleColor}.50` : isNotApplicable ? 'gray.25' : 'white'}
      >
        {!isEditable && (
          <Box alignItems="center" justifyContent="center" px={2} w="50px" h="full">
            <Pressable
              w={5}
              h={5}
              alignItems="center"
              justifyContent="center"
              borderWidth={isNotApplicable ? 1.5 : assignee?.employeeId ? 2 : 1}
              borderColor={isCompleted ? `${scheduleColor}.200` : assignee?.employeeId ? `${scheduleColor}.200` : 'gray.200'}
              bg={isCompleted ? `${scheduleColor}.500` : 'white'}
              rounded="md"
              shadow={assignee?.employeeId && !isCompleted ? 3 : 0}
              style={{
                ...(isNotApplicable && { borderStyle: 'dashed' }),
                ...((isNotApplicable || !assignee?.employeeId || !assignee?.id) && { cursor: 'not-allowed' }),
              }}
              _disabled={{ opacity: 0.6 }}
              _hover={{ borderColor: isNotApplicable || assignee?.employeeId ? `${scheduleColor}.200` : 'gray.500' }}
              disabled={isNotApplicable || !assignee?.employeeId || !assignee?.id || !siteTime.isTimeBetweenWeekBlocks(scheduleDate)}
              onPress={() => (assignee?.status === CleaningTaskStatusEnum.Incomplete ? handleCompletion(true) : modalConfirm.setOpen())}
            >
              {({ isHovered }) => {
                if (isNotApplicable)
                  return (
                    <Text size="md" color="gray.400" fontWeight={600} lineHeight="xs">
                      -
                    </Text>
                  );
                if (isCompleted) return <CheckIcon size={3} color="white" />;
                if (isHovered && !isNotApplicable) return <CheckIcon size={3} color="gray.300" />;
                return null;
              }}
            </Pressable>
          </Box>
        )}
        <HStack alignItems="center" justifyContent="space-between" space={3} py={1.5} px={2} pr={1} flex={1} h="full">
          <VStack flex={1} pt={isCompleted ? 1 : 0} space={2}>
            <Text
              size="md"
              fontWeight={600}
              color={isNotApplicable || isCompleted ? 'gray.500' : 'gray.900'}
              lineHeight="md"
              numberOfLines={2}
              strikeThrough={isNotApplicable}
            >
              {translate(task.title, task.translations)}
            </Text>
            {isDailyCleaning && !!task.positionTitle && (
              <Text size="sm" fontWeight={400} color={isNotApplicable || isCompleted ? 'gray.500' : 'gray.600'} lineHeight="xs">
                {translate(task.positionTitle, task.positionTitleTranslations)}
              </Text>
            )}
            {isCompleted && !!assignee?.statusDate && (
              <Text size="sm" color="gray.600" fontWeight={500}>
                {formatTo(assignee.statusDate, 'MMM DD h:mm A')}
              </Text>
            )}
          </VStack>

          {!isEditable && (
            <IconButton
              py={1.5}
              px={1}
              isDisabled={!task.linkedDoc}
              _disabled={{ _icon: { color: 'gray.400' } }}
              icon={<FileAttachmentIcon size="sm" />}
              onPress={handlePressLinkedDocument}
            />
          )}
        </HStack>

        <Box
          display={{ base: 'none', md: 'flex' }}
          justifyContent="center"
          px={2}
          width={isEditable ? '180px' : '16%'}
          minWidth={isEditable ? undefined : '105px'}
          maxWidth={isDailyCleaning ? '180px' : '250px'}
          h="full"
        >
          {isEditable ? (
            <PopoverBulkScheduleMenu
              isOpen={modalScheduleTime.isOpen}
              onOpen={modalScheduleTime.setOpen}
              onClose={modalScheduleTime.setHide}
              scheduleType={scheduleType}
              dopStartHour={isDailyCleaning ? dopStartHour : undefined}
              isLoading={addUpdateCleaningTask.isLoading && !task.assignee?.dueDate && assignee?.dueDate !== task.assignee?.dueDate}
              scheduledDate={assignee?.dueDate ?? scheduleDate}
              onApply={handleChangeDueDate}
              min={
                scheduleType === CleaningScheduleTypeEnum.Monthly
                  ? new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), 1)
                  : currentWeek.startDate
              }
              max={
                scheduleType === CleaningScheduleTypeEnum.Monthly
                  ? new Date(scheduleDate.getFullYear(), scheduleDate.getMonth() + 1, 0)
                  : currentWeek.endDate
              }
              trigger={triggerProps => (
                <Pressable
                  {...triggerProps}
                  isDisabled={isNotApplicable || isCompleted}
                  rounded="lg"
                  opacity={isNotApplicable ? 0.3 : 1}
                  onPress={modalScheduleTime.setOpen}
                >
                  <PressableSelect
                    hideClear
                    isDailyCleaning={isDailyCleaning}
                    isAutoScheduled={assignee?.isAutoScheduled}
                    colorScheme={scheduleColor}
                    _inputWrapper={{
                      bg: isCompleted ? `${scheduleColor}.50` : !assignee?.dueDate ? 'primary.50' : 'gray.10',
                      borderColor: isCompleted ? `${scheduleColor}.200` : !assignee?.dueDate ? 'primary.100' : 'gray.100',
                    }}
                    _text={
                      isNotApplicable
                        ? { color: 'gray.600', fontWeight: 400, size: 'sm' }
                        : !assignee?.dueDate
                        ? { color: 'primary.700', fontWeight: 600, size: 'sm' }
                        : { size: 'sm' }
                    }
                    isCompleted={isCompleted}
                    isDisabled
                    placeholder={isNotApplicable ? t('cleaning_task:no_time') : t('cleaning_task:select_date_and_time')}
                    value={formatTo(assignee?.dueDate, isDailyCleaning ? 'MMM DD h:mm A' : 'ddd, MMM DD h:mm A') ?? ''}
                  />
                </Pressable>
              )}
            />
          ) : isCompleted || isNotApplicable ? (
            <Box borderWidth={1} borderColor="transparent" rounded="md" px={1.5} py={1} w="full" minH="44px" justifyContent="center">
              {assignee?.dueDate ? (
                <VStack>
                  <Text size="sm" fontWeight={500} color={isNotApplicable ? 'gray.400' : 'gray.900'} lineHeight="xs">
                    {formatTo(assignee.dueDate, 'ddd MM/DD')}
                  </Text>
                  <Text size="sm" fontWeight={500} color={isNotApplicable ? 'gray.400' : 'gray.900'} lineHeight="xs">
                    {formatTo(assignee.dueDate, 'h:mm A')}
                  </Text>
                </VStack>
              ) : isNotApplicable ? (
                <Text size="sm" fontStyle="italic" color="gray.400">
                  N/A
                </Text>
              ) : null}
            </Box>
          ) : (
            <PopoverBulkScheduleMenu
              isOpen={modalScheduleTime.isOpen}
              onOpen={modalScheduleTime.setOpen}
              onClose={modalScheduleTime.setHide}
              scheduleType={scheduleType}
              dopStartHour={isDailyCleaning ? dopStartHour : undefined}
              isLoading={addUpdateCleaningTask.isLoading && !task.assignee?.dueDate && assignee?.dueDate !== task.assignee?.dueDate}
              scheduledDate={assignee?.dueDate ?? scheduleDate}
              onApply={handleChangeDueDate}
              min={
                scheduleType === CleaningScheduleTypeEnum.Monthly
                  ? new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), 1)
                  : currentWeek.startDate
              }
              max={
                scheduleType === CleaningScheduleTypeEnum.Monthly
                  ? new Date(scheduleDate.getFullYear(), scheduleDate.getMonth() + 1, 0)
                  : currentWeek.endDate
              }
              trigger={triggerProps => (
                <Pressable
                  {...triggerProps}
                  onPress={modalScheduleTime.setOpen}
                  borderWidth={1}
                  borderColor={!assignee?.dueDate ? 'primary.300' : 'gray.200'}
                  bg={!assignee?.dueDate ? 'primary.50' : 'white'}
                  rounded="md"
                  px={1.5}
                  py={1}
                  w="full"
                  minH="44px"
                  justifyContent="center"
                >
                  <HStack space={1.5} alignItems="center" justifyContent="flex-start" w="full">
                    <CalendarIcon size="sm" color={!assignee?.dueDate ? 'primary.600' : 'gray.500'} />
                    {assignee?.dueDate ? (
                      <VStack flex={1}>
                        <Text size="sm" fontWeight={500} color="gray.900" lineHeight="xs">
                          {formatTo(assignee.dueDate, 'ddd MM/DD')}
                        </Text>
                        <Text size="sm" fontWeight={500} color="gray.900" lineHeight="xs">
                          {formatTo(assignee.dueDate, 'h:mm A')}
                        </Text>
                      </VStack>
                    ) : (
                      <Text size="sm" fontWeight={600} color="primary.700">
                        {t('cleaning_task:schedule')}
                      </Text>
                    )}
                  </HStack>
                </Pressable>
              )}
            />
          )}
        </Box>

        <Box display={{ base: 'none', md: 'flex' }} justifyContent="center" px={2} w="22%" minWidth="115px" maxWidth="220px" h="full">
          {isEditable ? (
            <PressableSelect
              hideClear={isCompleted || isNotApplicable}
              isAutoAssigned={assignee?.isAutoAssigned}
              colorScheme={scheduleColor}
              _container={{ opacity: isNotApplicable ? 0.3 : 1 }}
              _inputWrapper={{
                bg: isCompleted && employeeName ? `${scheduleColor}.50` : !employeeName && assignee?.dueDate ? 'primary.50' : 'gray.25',
                borderColor:
                  isCompleted && employeeName ? `${scheduleColor}.200` : !employeeName && assignee?.dueDate ? 'primary.100' : 'gray.200',
              }}
              _text={{
                size: 'sm',
                color:
                  isCompleted && employeeName
                    ? 'gray.900'
                    : !employeeName && assignee?.dueDate
                    ? 'primary.700'
                    : employeeName
                    ? 'gray.900'
                    : 'gray.400',
              }}
              isDisabled={isNotApplicable || !assignee?.dueDate}
              isCompleted={isCompleted && !!employeeName}
              isLoading={
                addUpdateCleaningTask.isLoading && !task.assignee?.employeeId && assignee?.employeeId !== task.assignee?.employeeId
              }
              isReadOnly={isCompleted || isNotApplicable}
              placeholder={t('cleaning_task:assign_employee')}
              value={isNotApplicable ? undefined : employeeName}
              onPress={() => modalEmployee.setOpen(task)}
              onClear={handleDeleteAssignee}
            />
          ) : isCompleted || isNotApplicable ? (
            <Box borderWidth={1} borderColor="transparent" rounded="md" px={1.5} py={1} w="full" minH="44px" justifyContent="center">
              {employeeName ? (
                <Text size="sm" color={isNotApplicable ? 'gray.400' : 'gray.900'} fontWeight={500}>
                  {employeeName}
                </Text>
              ) : null}
            </Box>
          ) : assignee?.dueDate ? (
            <Pressable
              onPress={() => modalEmployee.setOpen(task)}
              borderWidth={1}
              borderColor={!employeeName ? 'primary.300' : 'gray.200'}
              bg={!employeeName ? 'primary.50' : 'white'}
              rounded="md"
              px={1.5}
              py={1}
              w="full"
              minH="44px"
              justifyContent="center"
            >
              <HStack space={1.5} alignItems="center" justifyContent="flex-start" w="full">
                <UserPenIcon size="sm" color={!employeeName ? 'primary.600' : 'gray.500'} />
                {employeeName ? (
                  <Text size="sm" color="gray.900" fontWeight={500} flex={1} numberOfLines={2}>
                    {employeeName}
                  </Text>
                ) : (
                  <Text size="sm" fontWeight={600} color="primary.700">
                    {t('cleaning_task:assign')}
                  </Text>
                )}
              </HStack>
            </Pressable>
          ) : (
            <Box borderWidth={1} borderColor="transparent" rounded="md" px={1.5} py={1} w="full" minH="44px" justifyContent="center" />
          )}
        </Box>
        <Box alignItems="center" justifyContent="center" w="70px" h="full">
          <SwitchButton
            style={
              {
                cursor: isCompleted || !!employeeName ? 'not-allowed' : 'pointer',
                opacity: isCompleted || !!employeeName ? 0.3 : 1,
              } as ViewStyle
            }
            isChecked={isNotApplicable}
            isDisabled={isCompleted || !!employeeName}
            onChange={handleToggleNotApplicable}
          />
        </Box>
      </HStack>

      <Modal
        hideClose
        isOpen={modalConfirm.isOpen}
        onClose={modalConfirm.setHide}
        size="md"
        _content={{ rounded: 'xl', p: 5, pt: 4, maxW: 400 }}
      >
        <ConfirmIncompleteTask onClose={modalConfirm.setHide} onConfirm={() => handleCompletion(false)} />
      </Modal>

      <Modal
        size="full"
        _content={{ rounded: '2xl', maxWidth: { md: 1080, xl: '95%' }, width: '70%', height: '95%', maxHeight: 900 }}
        isOpen={modalEmployee.isOpen}
        onClose={modalEmployee.setHide}
        noPadding
      >
        <EmployeeAssignment
          assignee={assignee}
          positionId={task.positionId}
          scheduleDate={assignee?.dueDate ?? scheduleDate}
          scheduleType={scheduleType}
          taskId={task.id!}
          taskPosition={translate(task.positionTitle, task.positionTitleTranslations)}
          taskName={translate(task.title, task.translations)}
          onChange={handleChangeAssignee}
          onClose={modalEmployee.setHide}
        />
      </Modal>
    </>
  );
};
